import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { 
    CHUNK_SIZE, WORLD_SIZE_IN_CHUNKS, MAP_RADIUS, WORLD_CHUNKS_ACROSS,
    WATER_LEVEL, BASE_HEIGHT, MIN_TERRAIN_HEIGHT, MAX_HEIGHT_VARIATION,
    MOUNTAIN_HEIGHT, VIEW_DISTANCE, VOXEL_SIZE, ISLAND_RADIUS, WATER_BORDER,
    DEFAULT_TERRAIN_SETTINGS, TERRAIN_PHYSICS
} from './TerrainConstants.js';
import { BiomeGenerator } from './BiomeGenerator.js';
import { BiomeManager } from './BiomeManager.js';
import { TerrainRules } from './TerrainRules.js';
import { TerrainFeatures } from './TerrainFeatures.js';
import { TerrainChunk } from './TerrainChunk.js';
import { TerrainMaterials } from './TerrainMaterials.js';
import { 
    getLocalCoordinates, 
    isWithinChunkBounds,
    interpolateHeight,
    createHeightMap
} from './TerrainUtils.js';
import IslandTypeManager from './IslandTypeManager.js';

/**
 * Manages terrain generation and chunk operations
 * Responsible for:
 * - Chunk setup and management
 * - Terrain mesh creation
 * - Delegating biome and heightmap tasks
 */
export class ChunkManager {
    constructor(scene, seed) {
        this.scene = scene;
        this.chunks = new Map();
        this.loadedChunks = new Set();
        this.terrainGroup = new THREE.Group();
        this.scene.add(this.terrainGroup);
        
        // Add neighbor caches to track adjacent chunks for blending
        this.chunkNeighborCache = new Map();

        // Initialize core properties
        this.worldSize = CHUNK_SIZE * WORLD_CHUNKS_ACROSS;
        this.viewDistance = VIEW_DISTANCE;
        this.settings = { ...DEFAULT_TERRAIN_SETTINGS };
        this.waterLevel = WATER_LEVEL;
        
        // Track the current island type
        this.islandType = IslandTypeManager.getInstance().getType();

        // Initialize with the provided seed or generate a random one
        this.seed = seed || Math.floor(Math.random() * 1000000);
        console.log(`[Terrain] Generated new random seed: ${this.seed}`);
        
        // Initialize biome systems with the seed
        this.biomeGenerator = new BiomeGenerator(this.seed);
        this.biomeManager = new BiomeManager(this.biomeGenerator);
        
        // Initialize materials system
        this.materials = new TerrainMaterials();
        this.features = new TerrainFeatures(scene, this.biomeGenerator, this.materials.getAllMaterials());

        // Add lighting
        this.setupLighting(scene);
        
        this.worldMode = 'dynamic';
        this.centerOffset = Math.floor(WORLD_CHUNKS_ACROSS / 2);
        this.isGenerating = false;
        this.chunksToLoad = new Set();

        // Error tracking
        this.errors = new Set();
        this.retryCount = 0;
        this.maxRetries = 3;

        this.initPhysics();
    }

    /**
     * Initializes physics world for terrain
     */
    initPhysics() {
        if (!window.physicsWorld) {
            const world = new CANNON.World({
                gravity: new CANNON.Vec3(0, TERRAIN_PHYSICS.gravity, 0)
            });
            world.broadphase = new CANNON.SAPBroadphase(world);
            world.defaultContactMaterial.friction = TERRAIN_PHYSICS.friction;
            world.defaultContactMaterial.restitution = TERRAIN_PHYSICS.restitution;
            window.physicsWorld = world;
        }
    }

    /**
     * Sets up lighting for the terrain
     * @param {THREE.Scene} scene - The Three.js scene
     */
    setupLighting(scene) {
        const ambient = new THREE.AmbientLight(0x404040, 0.5);
        const directional = new THREE.DirectionalLight(0xffffff, 0.8);
        directional.position.set(100, 100, 50);
        directional.castShadow = true;
        scene.add(ambient, directional);
    }

    /**
     * Checks if coordinates are within world bounds
     * @param {number} x - X coordinate
     * @param {number} y - Y coordinate
     * @returns {boolean} Whether coordinates are valid
     */
    isWithinWorldBounds(x, y) {
        return x >= 0 && x < WORLD_CHUNKS_ACROSS &&
               y >= 0 && y < WORLD_CHUNKS_ACROSS;
    }

    /**
     * Gets or creates a chunk at the specified coordinates
     * @param {number} x - Chunk X coordinate
     * @param {number} y - Chunk Y coordinate
     * @returns {TerrainChunk|null} The chunk or null if invalid
     */
    getChunkAt(x, y) {
        try {
            if (!this.isWithinWorldBounds(x, y)) return null;

            const key = `${x},${y}`;
            
            // Check if we need to load adjacent chunks for better blending
            this.preloadNeighborChunks(x, y);
            
            if (!this.chunks.has(key)) {
                // Pass adjacent chunk info to the new chunk for better boundary blending
                const neighborHeights = this.getNeighborHeightData(x, y);
                
                // Create settings with current island type
                const chunkSettings = { 
                    ...this.settings,
                    islandType: this.islandType // Pass the current island type
                };
                
                const chunk = new TerrainChunk(
                    x, y, 
                    chunkSettings, 
                    this.materials.getAllMaterials(),
                    // Pass optional neighbor height data for better blending
                    neighborHeights
                );
                
                if (!chunk.mesh) {
                    this.errors.add(key);
                    if (this.retryCount < this.maxRetries) {
                        this.retryCount++;
                        console.warn(`Retrying chunk generation at ${key}`);
                        return this.getChunkAt(x, y);
                    }
                    console.warn(`Failed to generate mesh for chunk ${key}`);
                    return null;
                }
                this.chunks.set(key, chunk);
                
                // Store chunk in neighbor cache for surrounding chunks to use
                this.updateNeighborCache(x, y, chunk);
            }
            
            const chunk = this.chunks.get(key);
            if (chunk?.mesh) {
                this.terrainGroup.add(chunk.mesh);
            }
            return chunk;
        } catch (error) {
            console.error(`Error generating chunk at ${x},${y}:`, error);
            return null;
        }
    }

    /**
     * Pre-loads neighboring chunks to ensure seamless boundaries
     * @param {number} x - Center chunk X coordinate
     * @param {number} y - Center chunk Y coordinate 
     */
    preloadNeighborChunks(x, y) {
        // Load only immediate neighbors in a 3x3 grid
        for (let nx = x - 1; nx <= x + 1; nx++) {
            for (let ny = y - 1; ny <= y + 1; ny++) {
                // Skip the center chunk (that's what we're trying to generate)
                if (nx === x && ny === y) continue;
                
                // Only try to load if it's within world bounds
                if (this.isWithinWorldBounds(nx, ny)) {
                    const neighborKey = `${nx},${ny}`;
                    // Preload neighbor but don't recursively load more neighbors
                    if (!this.chunks.has(neighborKey)) {
                        // Just create the chunk without triggering further loads
                        try {
                            // Create settings with current island type
                            const chunkSettings = { 
                                ...this.settings,
                                islandType: this.islandType
                            };
                            
                            // Using a simpler variation to avoid recursion
                            const neighborChunk = new TerrainChunk(
                                nx, ny, 
                                chunkSettings, 
                                this.materials.getAllMaterials()
                            );
                            
                            if (neighborChunk?.mesh) {
                                this.chunks.set(neighborKey, neighborChunk);
                                // Update the neighbor cache with this new chunk
                                this.updateNeighborCache(nx, ny, neighborChunk);
                            }
                        } catch (error) {
                            // Just log but don't fail - we can still generate the main chunk
                            console.warn(`Failed to preload neighbor chunk at ${nx},${ny}:`, error);
                        }
                    }
                }
            }
        }
    }

    /**
     * Updates the neighbor cache with a newly created chunk
     * @param {number} x - Chunk X coordinate
     * @param {number} y - Chunk Y coordinate
     * @param {TerrainChunk} chunk - The chunk to cache
     */
    updateNeighborCache(x, y, chunk) {
        const key = `${x},${y}`;
        this.chunkNeighborCache.set(key, chunk);
        
        // Keep cache size under control by removing entries far from loaded chunks
        if (this.chunkNeighborCache.size > 100) {
            // Simple strategy: remove oldest entries
            const keysToRemove = Array.from(this.chunkNeighborCache.keys()).slice(0, 10);
            keysToRemove.forEach(k => this.chunkNeighborCache.delete(k));
        }
    }

    /**
     * Gets height data from neighboring chunks for seamless transitions
     * @param {number} x - Chunk X coordinate
     * @param {number} y - Chunk Y coordinate
     * @returns {Object|null} Neighbor height data or null
     */
    getNeighborHeightData(x, y) {
        // Create an object to hold neighbor height data
        const neighbors = {
            north: null,
            south: null,
            east: null,
            west: null,
            northeast: null,
            northwest: null,
            southeast: null,
            southwest: null
        };
        
        // Direction offsets
        const dirs = [
            {key: 'north', dx: 0, dy: -1},
            {key: 'south', dx: 0, dy: 1},
            {key: 'east', dx: 1, dy: 0},
            {key: 'west', dx: -1, dy: 0},
            {key: 'northeast', dx: 1, dy: -1},
            {key: 'northwest', dx: -1, dy: -1},
            {key: 'southeast', dx: 1, dy: 1},
            {key: 'southwest', dx: -1, dy: 1}
        ];
        
        // Check each neighboring direction
        for (const dir of dirs) {
            const nx = x + dir.dx;
            const ny = y + dir.dy;
            
            if (this.isWithinWorldBounds(nx, ny)) {
                const neighborKey = `${nx},${ny}`;
                const neighborChunk = this.chunks.get(neighborKey) || this.chunkNeighborCache.get(neighborKey);
                
                if (neighborChunk && neighborChunk.heightMap) {
                    // Store the heightmap from this neighbor
                    neighbors[dir.key] = neighborChunk.heightMap;
                }
            }
        }
        
        return neighbors;
    }

    /**
     * Loads all chunks for editor mode
     */
    loadAllChunksForEditor() {
        console.log('Loading all chunks for editor mode...');
        const totalChunks = WORLD_CHUNKS_ACROSS * WORLD_CHUNKS_ACROSS;
        let loadedCount = 0;

        // Clear existing loaded chunks to prevent duplicates
        this.loadedChunks.clear();

        for (let x = 0; x < WORLD_CHUNKS_ACROSS; x++) {
            for (let z = 0; z < WORLD_CHUNKS_ACROSS; z++) {
                const chunk = this.getChunkAt(x, z);
                if (chunk?.mesh) {
                    this.scene.add(chunk.mesh);
                    this.loadedChunks.add(`${x},${z}`);
                    loadedCount++;
                }
            }
        }

        console.log(`Editor mode: Loaded ${loadedCount}/${totalChunks} chunks`);
    }

    /**
     * Sets the world mode (editor or dynamic)
     * @param {string} mode - 'editor' or 'dynamic'
     */
    setWorldMode(mode) {
        if (this.worldMode === mode) return; // Don't reload if mode hasn't changed
        
        this.worldMode = mode;
        if (mode === 'editor') {
            this.loadAllChunksForEditor();
        } else if (mode === 'dynamic') {
            // Clear loaded chunks but don't remove them from scene
            // This allows dynamic loading to take over
            this.loadedChunks.clear();
        }
    }

    /**
     * Updates chunks based on player position
     * @param {THREE.Vector3} playerPosition - Current player position
     */
    updateChunks(playerPosition) {
        // Skip chunk updates in editor mode
        if (!playerPosition || this.worldMode === 'editor') return;

        const chunkX = Math.floor(playerPosition.x / CHUNK_SIZE);
        const chunkZ = Math.floor(playerPosition.z / CHUNK_SIZE);
        
        // Only update chunks at most once every 8 frames for performance
        if (!this.lastUpdateFrame || (window.frameCount - this.lastUpdateFrame > 8)) {
            this.lastUpdateFrame = window.frameCount || 0;
        } else {
            // Skip this update to prevent CPU overload
            return;
        }

        const previousLoadedCount = this.loadedChunks.size;
        const expectedChunks = Math.pow(this.viewDistance * 2 + 1, 2);
        
        // Sort chunks by distance from player for prioritized loading
        const chunksToLoad = [];
        
        // Collect chunks to load
        for (let x = -this.viewDistance; x <= this.viewDistance; x++) {
            for (let z = -this.viewDistance; z <= this.viewDistance; z++) {
                const targetX = chunkX + x;
                const targetZ = chunkZ + z;
                const key = `${targetX},${targetZ}`;
                
                // Calculate distance from player (for sorting)
                const distance = Math.sqrt(x*x + z*z);

                if (!this.loadedChunks.has(key) && this.isWithinWorldBounds(targetX, targetZ)) {
                    chunksToLoad.push({
                        x: targetX,
                        z: targetZ,
                        key,
                        distance
                    });
                }
            }
        }

        // Sort chunks by distance from player
        chunksToLoad.sort((a, b) => a.distance - b.distance);

        // Load chunks
        for (const { x, z, key } of chunksToLoad) {
            const chunk = this.getChunkAt(x, z);
            if (chunk?.mesh) {
                this.terrainGroup.add(chunk.mesh);
                this.loadedChunks.add(key);
            }
        }

        const newLoadedCount = this.loadedChunks.size;
        console.log(`Updated chunks: Loaded ${newLoadedCount - previousLoadedCount}/${expectedChunks} chunks`);
    }

    /**
     * Sets the current island type for terrain generation
     * @param {number} type - The island type (0-5)
     */
    setIslandType(type) {
        if (type >= 0 && type <= 5) {
            console.log(`[ChunkManager] Setting island type to: ${type}`);
            
            // Update the island type in the manager
            IslandTypeManager.getInstance().setType(type);
            
            // Store locally as well for backward compatibility
            this.islandType = type;
            
            // If we've already generated chunks, we need to clear and regenerate them
            if (this.loadedChunks.size > 0) {
                console.log('[ChunkManager] Island type changed, chunks will be regenerated as needed');
            }
        } else {
            console.warn(`[ChunkManager] Invalid island type: ${type}`);
        }
    }

    /**
     * Gets terrain height at a world position with bilinear interpolation for smooth values
     * @param {number} worldX - World X coordinate
     * @param {number} worldZ - World Z coordinate
     * @returns {number} Interpolated terrain height
     */
    getHeightAt(worldX, worldZ) {
        const chunkX = Math.floor(worldX / CHUNK_SIZE);
        const chunkZ = Math.floor(worldZ / CHUNK_SIZE);

        if (!this.isWithinWorldBounds(chunkX, chunkZ)) {
            return WATER_LEVEL;
        }

        const chunk = this.getChunkAt(chunkX, chunkZ);
        if (!chunk) return WATER_LEVEL;

        // Use utility function to get local coordinates
        const { localX, localZ } = getLocalCoordinates(worldX, worldZ, chunkX, chunkZ);
        
        try {
            // Get heights at four corners for bilinear interpolation
            const h00 = chunk.getHeightAt(Math.floor(localX), Math.floor(localZ)) || WATER_LEVEL;
            const h10 = chunk.getHeightAt(Math.min(Math.ceil(localX), CHUNK_SIZE - 1), Math.floor(localZ)) || WATER_LEVEL;
            const h01 = chunk.getHeightAt(Math.floor(localX), Math.min(Math.ceil(localZ), CHUNK_SIZE - 1)) || WATER_LEVEL;
            const h11 = chunk.getHeightAt(
                Math.min(Math.ceil(localX), CHUNK_SIZE - 1), 
                Math.min(Math.ceil(localZ), CHUNK_SIZE - 1)
            ) || WATER_LEVEL;
            
            // Calculate fractional parts for interpolation weights
            const fx = localX - Math.floor(localX);
            const fz = localZ - Math.floor(localZ);
            
            // Perform bilinear interpolation
            const height = interpolateHeight(h00, h10, h01, h11, fx, fz);
            
            // Ensure height is valid
            return isFinite(height) ? Math.max(height, WATER_LEVEL) : WATER_LEVEL;
        } catch (error) {
            console.error('Error calculating terrain height:', error);
            return WATER_LEVEL;
        }
    }
}